// Generated by tutorials/record.cjs from tutorials/articles/h10-getting-around.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h10 --text-only` (wording) or
// `node tutorials/record.cjs h10` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'getting-around',
  catalogId: 'h10',
  title: 'Getting around Dietech Online',
  summary: 'Where everything lives: the menu on the left, the search box at the top, your dashboard, your facility and your account. Start here if you are new to Dietech Online.',
  audience: 'Everyone who signs in to Dietech Online',
  area: 'general',
  learn: [
    'Open a section and its pages from the menu',
    'Pin the pages you use most',
    'Find any page by typing its name',
    'Get back to your dashboard',
    'Find your facility, your account and help',
  ],
  keywords: [
    'menu',
    'navigation',
    'sidebar',
    'sections',
    'find a page',
    'menu filter',
    'pin',
    'pinned',
    'recent',
    'search',
    'home',
    'tour',
    'new user',
    'getting started',
  ],
  steps: [
    {
      id: 'sections',
      part: 'Use the menu on the left',
      heading: 'The menu is on the left',
      text: 'Everything in Dietech Online is in the menu on the left. The column of icons holds the sections: Home, Residents, Clinical, Menus, Food Data, Kitchen, Purchasing, Reports and Setup.',
      media: {
        image: {
          src: '/help/getting-around/sections.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The column of section icons down the left side of the screen, from Home to Setup.',
      },
    },
    {
      id: 'open-section',
      part: 'Use the menu on the left',
      heading: 'Open a section',
      text: 'Select a section to open its main page. The pages in that section are listed beside the icons, in groups.',
      media: {
        image: {
          src: '/help/getting-around/open-section.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/getting-around/open-section.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.9,
        },
        alt: 'Residents is selected, and its pages are listed beside the icons: Residents, Census, Guests, Weight Entry and more.',
      },
    },
    {
      id: 'open-page',
      part: 'Use the menu on the left',
      heading: 'Open a page',
      text: 'Select a page in the list to open it. The page you are on is marked in the list, and its name is at the top of the screen.',
      media: {
        image: {
          src: '/help/getting-around/open-page.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/getting-around/open-page.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.1,
        },
        alt: 'Weight Entry is selected in the list of Residents pages, and the Weight Entry page is open.',
      },
    },
    {
      id: 'pin',
      part: 'Use the menu on the left',
      heading: 'Pin the pages you use most',
      text: 'Point to a page in the list and select the star beside it to pin it. Your pinned pages are listed under Home, with the pages you opened recently, so they are always one click away.',
      media: {
        image: {
          src: '/help/getting-around/pin.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/getting-around/pin.mp4',
          width: 1280,
          height: 748,
          durationSec: 5.8,
        },
        alt: 'The Home list with Weight Entry under Pinned, marked with a gold star, and Residents under Recent.',
      },
    },
    {
      id: 'collapse',
      part: 'Use the menu on the left',
      heading: 'Make more room',
      text: 'Select the double arrow at the bottom of the menu to hide the list of pages and give your work more room. The section icons stay. Select it again to bring the list back.',
      media: {
        image: {
          src: '/help/getting-around/collapse.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/getting-around/collapse.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.7,
        },
        alt: 'The menu folded down to the column of section icons, with the double arrow at the bottom.',
      },
    },
    {
      id: 'search-page',
      part: 'Find a page by name',
      heading: 'Type the name of a page',
      text: 'Know the page you want? Select the search box at the top, or press Ctrl and / together, and type part of its name. Pages are listed first, then reports, then residents.',
      media: {
        image: {
          src: '/help/getting-around/search-page.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/getting-around/search-page.mp4',
          width: 1280,
          height: 748,
          durationSec: 5,
        },
        alt: 'The search box with "census" typed in it. Census is listed under Pages, and the census reports under Reports.',
      },
    },
    {
      id: 'search-open',
      part: 'Find a page by name',
      heading: 'Open it',
      text: 'Select a result, or use the arrow keys and press Enter, to open it.',
      media: {
        image: {
          src: '/help/getting-around/search-open.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/getting-around/search-open.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.2,
        },
        alt: 'The Census page, opened from the search box.',
      },
    },
    {
      id: 'dashboard',
      part: 'Your dashboard, facility and account',
      heading: 'Back to your dashboard',
      text: 'Select Home to go back to your dashboard. It shows what changed in your facility since your last shift. Your dashboard has an article of its own.',
      media: {
        image: {
          src: '/help/getting-around/dashboard.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/getting-around/dashboard.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.7,
        },
        alt: 'The dashboard: the current meal and census across the top, shortcuts below, then cards of recent changes.',
      },
    },
    {
      id: 'facility',
      part: 'Your dashboard, facility and account',
      heading: 'Your facility',
      text: 'The facility you are working in is shown at the top right. If you work in more than one, select it to switch.',
      media: {
        image: {
          src: '/help/getting-around/facility.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/getting-around/facility.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.2,
        },
        alt: 'The Switch Facility list open under the facility name at the top right, with Maple Grove Care Center marked Current.',
      },
    },
    {
      id: 'account',
      part: 'Your dashboard, facility and account',
      heading: 'Your account',
      text: 'Select your name at the top right to see your role and facility, change the size of everything on screen, open your settings, or log out.',
      media: {
        image: {
          src: '/help/getting-around/account.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/getting-around/account.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.9,
        },
        alt: 'The menu under the user\'s name: role, facility and user name, then size, font and color choices, My Settings and Help. Log Out is further down.',
      },
    },
    {
      id: 'help',
      part: 'Your dashboard, facility and account',
      heading: 'Help is on every page',
      text: 'Select Help at the top of any page for the help about that page.\n\nAlong the bottom, Knowledge Base opens more articles, and Start Support Session lets PSI Support see your screen while you are on the phone with them.',
      media: {
        image: {
          src: '/help/getting-around/help.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Help button at the top of the screen marked, and Knowledge Base and Start Support Session along the bottom.',
      },
    },
  ],
  tips: [
    'Press Ctrl and / together on any page to jump straight to the search box.',
    'Select the Dietech logo at the top left to go back to your dashboard from anywhere.',
    'A page that is greyed out in the menu is one your role or your facility does not use. Point to it to see why.',
  ],
  notes: [
    'Pinned and recent pages belong to you. Other people who sign in see their own.',
    'The menu only lists what your role allows. If a page you need is missing, ask your administrator.',
  ],
  related: ['your-dashboard', 'finding-a-resident', 'switching-facility', 'settings-and-preferences'],
  recorded: '2026-10-25',
};

export default article;
